import { Button, Card, Grid, Modal } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import CreateEditForm from '../../Products/CreateEditForm';
import LoaderOverlay from '../../../components/LoaderOverlay/LoaderOverlay';
import { trackEvent } from '../../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../../utils/monitoring/Monitoring_Events_Enum';
import { usePostHog } from 'posthog-js/react';
import { useForm } from '@mantine/form';
import { requiredFieldFormValidator } from '../../../utils/validators/formValidators';
import { showNotification } from '../../../utils/Notification/Notification';
import { useLocation } from 'react-router-dom';
import { IProduct } from '../../../interfaces/Product';
import { useCreateProduct } from '../../../hooks/products/mutations';
import { normalizeMinimumBillableQuantity } from '@washday/utilities';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const NewProductModal = ({
  opened,
  onClose,
  afterSubmit
}: {
  opened: boolean;
  onClose: () => void;
  afterSubmit: (product: IProduct) => void;
}) => {
  const { t } = useTranslation([
    'NewOrder',
    'FieldName',
    'ProductsPage',
    'FormValidator',
    'CommonButtons'
  ]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const searchParamSectionId = searchParams.get('section') || '';
  const posthog = usePostHog();
  const { user } = useSelector((state) => state.user);
  const canManageCompanyCatalog = Boolean(user?.isCompanyAdmin);
  const { mutateAsync: createAsync, isPending: isCreatePending } = useCreateProduct();
  const [createSessionKey, setCreateSessionKey] = useState(0);

  const form = useForm({
    initialValues: {
      name: '',
      description: '',
      type: 'normal',
      minimumBillableQuantity: 0,
      price: undefined,
      expressPrice: 0,
      pieces: 1,
      sku: '',
      overlayText: '',
      order: 0,
      taxExemptOne: false,
      taxExemptTwo: false,
      taxExemptThree: false,
      showInApp: false,
      image: null,
      imageName: '',
      section: searchParamSectionId || '',
      subcategory: null,
      store: canManageCompanyCatalog ? null : searchParamStoreId,
      isActive: true,
      invoice_description: '',
      invoice_product_key: '',
      invoice_product_unit_key: '',
      invoice_product_unit_name: '',
      productSupplies: [],
      isOutsourced: false,
      outsourcingPartner: null,
      barcode: '',
      priceOptions: []
    },
    validate: {
      name: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      type: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      minimumBillableQuantity: (value, values) => {
        try {
          normalizeMinimumBillableQuantity(value, values.type);
          return null;
        } catch {
          return t('FormValidator:minimumBillableQuantity');
        }
      },
      price: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      expressPrice: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      pieces: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      order: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      taxExemptOne: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      taxExemptTwo: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      taxExemptThree: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      showInApp: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      section: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      isActive: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      barcode: (value) =>
        value && !/^[a-z0-9\-]+$/.test(value) ? t('FormValidator:barcode') : null
    }
  });

  type FormValues = typeof form.values;
  const setFormValues = form.setValues;

  const submit = async (values: FormValues) => {
    try {
      const newProductResponse = await createAsync({
        ...values,
        minimumBillableQuantity: normalizeMinimumBillableQuantity(
          values.minimumBillableQuantity,
          values.type
        )
      } as any);
      showNotification({ type: 'Default', title: t('Notifications:success') });
      trackEvent(posthog, MonitoringEventTypes.POS_PAGE_PRODUCT_CREATED_SUCCESS, {});
      form.reset();
      afterSubmit(newProductResponse);
    } catch (e) {
      trackEvent(posthog, MonitoringEventTypes.POS_PAGE_PRODUCT_CREATED_FAIL, {
        error: e,
        data: values
      });
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  useEffect(() => {
    if (opened) {
      setFormValues({
        name: '',
        description: '',
        type: 'normal',
        minimumBillableQuantity: 0,
        price: undefined,
        expressPrice: 0,
        pieces: 1,
        section: searchParamSectionId || '',
        subcategory: null,
        store: canManageCompanyCatalog ? null : searchParamStoreId,
        isOutsourced: false,
        outsourcingPartner: null
      });
      setCreateSessionKey((currentKey) => currentKey + 1);
    }
  }, [
    canManageCompanyCatalog,
    opened,
    searchParamSectionId,
    searchParamStoreId,
    setFormValues
  ]);

  return (
    <Modal opened={opened} onClose={onClose} title={t('NewOrder:addProductCardTitle')} size="xl">
      <LoaderOverlay visible={isCreatePending} />
      <form onSubmit={form.onSubmit(submit)}>
        <CreateEditForm
          key={createSessionKey}
          form={form}
          storeId={searchParamStoreId}
          loading={false}
          canManageCompanyCatalog={canManageCompanyCatalog}
        />
        <Card pos={'sticky'} bottom={5}>
          <Card.Section inheritPadding py="xs">
            <Grid align="center">
              <Grid.Col span={{ xs: 12, md: 6 }}>
                <Button
                  fullWidth
                  color="gray"
                  loading={isCreatePending}
                  disabled={isCreatePending}
                  onClick={onClose}>
                  {t('CommonButtons:cancel')}
                </Button>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 6 }}>
                <Button fullWidth type="submit" loading={isCreatePending}>
                  {t('CommonButtons:submit')}
                </Button>
              </Grid.Col>
            </Grid>
          </Card.Section>
        </Card>
      </form>
    </Modal>
  );
};

export default NewProductModal;
